<template>
  <v-card class="ps-card" flat>
    <div class="ps-card__head">
      <v-icon small>mdi-credit-card-outline</v-icon>
      Gateway-Gebühren
      <span class="ps-card__hint">Fix + Rate je Zahlungsanbieter und Währung</span>
      <v-spacer />
      <v-btn v-if="!showAddGateway && !selectedGateway" @click="showAddGateway = true" color="primary" depressed small rounded>
        <v-icon small left>mdi-plus</v-icon>Gateway
      </v-btn>
      <div class="d-flex flex-nowrap justify-end align-center" v-if="showAddGateway">
        <v-text-field dense hide-details class="mr-3" outlined style="max-width: 140px" label="Gateway-Name" v-model="gateWayToAdd" />
        <v-btn :disabled="isAddingGateWay" :loading="isAddingGateWay" @click="addGateWay" class="mr-2" color="primary" depressed small rounded>Speichern</v-btn>
        <v-btn :disabled="isAddingGateWay" @click="showAddGateway = false" text small rounded>Abbrechen</v-btn>
      </div>
      <div class="d-flex flex-nowrap justify-end align-center" v-if="selectedGateway">
        <span class="ps-chip ps-chip--primary mr-3">Bearbeite: {{ selectedGateway.id }}</span>
        <v-btn :loading="isSaving" :disabled="isSaving" @click="saveGateway" class="mr-2" color="primary" depressed small rounded>Speichern</v-btn>
        <v-btn :disabled="isSaving" @click="cancelEdit" text small rounded>Abbrechen</v-btn>
      </div>
    </div>
    <v-card-text>
      <div class="pv-table-wrap">
        <table class="pv-table">
          <thead>
            <tr>
              <th class="text-left">Gateway</th>
              <th class="text-left">Währung</th>
              <th class="text-left">Fix</th>
              <th class="text-left">Rate</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <template v-for="gateway in displayGateways">
              <tr
                v-for="(charge, index) in gateway.charges"
                :key="gateway.id + '-' + charge.currency"
                :class="{ 'gw-row--editing': isEditing(gateway) }"
              >
                <td class="pv-table__label">
                  <template v-if="index === 0">{{ gateway.id }}</template>
                </td>
                <td style="width: 140px">
                  <span v-if="!isEditing(gateway)" class="ps-chip">{{ charge.currency.toUpperCase() }}</span>
                  <v-text-field v-else dense hide-details solo flat background-color="#f4f5f7" v-model="charge.currency" />
                </td>
                <td style="width: 140px">
                  <span v-if="!isEditing(gateway)" class="ps-price">{{ charge.fixed }}</span>
                  <v-text-field v-else dense hide-details solo flat background-color="#f4f5f7" type="number" step="0.01" v-model.number="charge.fixed" />
                </td>
                <td style="width: 140px">
                  <span v-if="!isEditing(gateway)" class="ps-price">{{ charge.rate }}</span>
                  <v-text-field v-else dense hide-details solo flat background-color="#f4f5f7" type="number" step="0.001" v-model.number="charge.rate" />
                </td>
                <td class="text-right" style="width: 90px">
                  <v-btn
                    v-if="index === 0 && !selectedGateway"
                    icon small
                    @click="editGateway(gateway)"
                  ><v-icon small>mdi-pencil</v-icon></v-btn>
                  <v-btn
                    v-if="isEditing(gateway)"
                    icon small color="error"
                    @click="deleteCurrencyCharges(charge.currency)"
                  ><v-icon small>mdi-delete-outline</v-icon></v-btn>
                </td>
              </tr>
              <tr v-if="!gateway.charges.length" :key="gateway.id + '-empty'">
                <td class="pv-table__label">{{ gateway.id }}</td>
                <td colspan="3"><span class="ps-muted">keine Währungen</span></td>
                <td class="text-right" style="width: 90px">
                  <v-btn v-if="!selectedGateway" icon small @click="editGateway(gateway)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </td>
              </tr>
              <tr v-if="isEditing(gateway) && availableCurrencies.length" :key="gateway.id + '-add'" class="gw-row--add">
                <td />
                <td>
                  <v-autocomplete dense hide-details solo flat background-color="#f4f5f7" :items="availableCurrencies" v-model="newCurrency.currency" placeholder="Währung" />
                </td>
                <td>
                  <v-text-field dense hide-details solo flat background-color="#f4f5f7" type="number" step="0.01" v-model.number="newCurrency.fixed" placeholder="Fix" />
                </td>
                <td>
                  <v-text-field dense hide-details solo flat background-color="#f4f5f7" type="number" step="0.001" v-model.number="newCurrency.rate" placeholder="Rate" />
                </td>
                <td class="text-right">
                  <v-btn icon small color="primary" :disabled="!newCurrency.currency" @click="addCurrencyCharges">
                    <v-icon small>mdi-plus-circle</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { db } from "../../../../fb";
import { doc, setDoc } from "firebase/firestore";
import _ from "lodash";

export default {
  name: "GatewayCharges",
  props: {
    settings: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      showAddGateway: false,
      isSaving: false,
      selectedGateway: null,
      gateWayToAdd: "",
      isAddingGateWay: false,
      newCurrency: this.getInitialCurrencyState(),
    };
  },
  computed: {
    gateways() {
      return Object.entries(this.settings.gatewayCharges).map(([id, charges]) => ({
        id,
        charges: Object.entries(charges).map(([currency, details]) => ({
          currency,
          ...details
        }))
      })).sort((a, b) => a.id.localeCompare(b.id));
    },
    /** Im Edit-Modus die editierbare Kopie des gewählten Gateways einblenden */
    displayGateways() {
      return this.gateways.map((gateway) =>
        this.isEditing(gateway) ? this.selectedGateway : gateway
      );
    },
    availableCurrencies() {
      if (!this.selectedGateway) return [];
      const usedCurrencies = this.selectedGateway.charges.map(c => c.currency);
      return Object.keys(this.settings.exchangeRates).filter(currency => !usedCurrencies.includes(currency));
    }
  },
  methods: {
    getInitialCurrencyState() {
      return {
        currency: "",
        fixed: 0,
        rate: 0,
      };
    },
    isEditing(gateway) {
      return this.selectedGateway && this.selectedGateway.id === gateway.id;
    },
    editGateway(gateway) {
      this.selectedGateway = _.cloneDeep(gateway);
    },
    cancelEdit() {
      this.selectedGateway = null;
      this.newCurrency = this.getInitialCurrencyState();
    },
    addCurrencyCharges() {
      this.selectedGateway = {
        ...this.selectedGateway,
        charges: [
          ...this.selectedGateway.charges,
          this.newCurrency
        ]
      }
      this.newCurrency = this.getInitialCurrencyState();
    },
    deleteCurrencyCharges(currency) {
      this.selectedGateway = {
        ...this.selectedGateway,
        charges: [
          ...this.selectedGateway.charges.filter(charge => charge.currency !== currency)
        ]
      }
    },
    async addGateWay() {
      this.isAddingGateWay = true
      this.selectedGateway = {
        id: this.gateWayToAdd,
        charges: []
      }

      await this.saveGateway()
      this.isAddingGateWay = false
      this.showAddGateway = false
    },
    async saveGateway() {
      if (!this.selectedGateway) return;

      this.isSaving = true;
      const docRef = doc(db, 'settings', 'statistics');

      const transformedCharges = this.selectedGateway.charges.reduce((acc, { currency, fixed, rate }) => {
        acc[currency] = { fixed, rate };
        return acc;
      }, {});

      // Replace the entire gatewayCharges object for the specific selectedGateway.id
      const updatedGatewayCharges = {
        ...this.settings.gatewayCharges,
        [this.selectedGateway.id]: transformedCharges
      };

      await setDoc(docRef, {
        ...this.settings,
        gatewayCharges: updatedGatewayCharges
      });

      this.cancelEdit();
      this.isSaving = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.gw-row--editing {
  background: rgba(63, 157, 104, 0.05);
}

.gw-row--add td {
  border-top: 2px dashed rgba(0, 0, 0, 0.12);
}
</style>
